import React, { useState, useEffect } from 'react';
import './PlanifSync.css';
import { API_URL } from '../config/api';

const PlanifSync = () => {
  const [config, setConfig] = useState({
    token_url: '',
    api_base_url: '',
    agents_endpoint: '/agents/actifs',
    auth_format: 'form'
  });
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/planif/config`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la config:', error);
    }
  };

  const saveConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/planif/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });

      if (response.ok) {
        alert('Configuration sauvegardée! Le serveur va recharger la configuration.');
        setTestResult(null);
        setSyncResult(null);
      } else {
        const data = await response.json();
        setError(data.message || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setLoading(true);
    setError(null);
    setTestResult(null);
    try {
      const response = await fetch(`${API_URL}/api/planif/test-connection`, {
        method: 'GET',
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setTestResult(data);
      } else {
        setError(data.message || 'Erreur de connexion');
        setTestResult({ success: false, ...data });
      }
    } catch (error) {
      setError('Erreur de connexion au serveur');
      setTestResult({ success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const syncUsers = async () => {
    setLoading(true);
    setError(null);
    setSyncResult(null);
    try {
      const response = await fetch(`${API_URL}/api/planif/sync-users`, {
        method: 'POST',
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setSyncResult(data);
      } else {
        setError(data.message || 'Erreur lors de la synchronisation');
        setSyncResult({ success: false, ...data });
      }
    } catch (error) {
      setError('Erreur de connexion au serveur');
      setSyncResult({ success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="planif-sync">
      <h1>🔄 Synchronisation Planif</h1>
      <p className="subtitle">Configurez et synchronisez les agents actifs depuis Planif</p>

      {/* Configuration */}
      <div className="config-section card">
        <h2>⚙️ Configuration de l'API</h2>
        <p className="info-text">
          Configurez les URLs selon le PDF "API PLANNING.pdf"
        </p>

        <div className="form-group">
          <label htmlFor="token_url">Token URL (OAuth2)</label>
          <input
            type="text"
            id="token_url"
            value={config.token_url}
            onChange={(e) => setConfig({ ...config, token_url: e.target.value })}
            placeholder="https://api.planif.com/oauth/token"
          />
        </div>

        <div className="form-group">
          <label htmlFor="api_base_url">API Base URL</label>
          <input
            type="text"
            id="api_base_url"
            value={config.api_base_url}
            onChange={(e) => setConfig({ ...config, api_base_url: e.target.value })}
            placeholder="https://api.planif.com/api"
          />
        </div>

        <div className="form-group">
          <label htmlFor="agents_endpoint">Endpoint Agents Actifs</label>
          <input
            type="text"
            id="agents_endpoint"
            value={config.agents_endpoint}
            onChange={(e) => setConfig({ ...config, agents_endpoint: e.target.value })}
            placeholder="/agents/actifs"
          />
        </div>

        <div className="form-group">
          <label htmlFor="auth_format">Format d'authentification</label>
          <select
            id="auth_format"
            value={config.auth_format}
            onChange={(e) => setConfig({ ...config, auth_format: e.target.value })}
          >
            <option value="form">Form (application/x-www-form-urlencoded)</option>
            <option value="json">JSON (application/json)</option>
          </select>
        </div>

        <div className="credentials-info">
          <p><strong>Identifiants configurés:</strong></p>
          <p>Client ID: <code>BALZAC</code></p>
          <p>Client Secret: <code>Fjhdfef5fezufFZ@dhd104HFd</code></p>
        </div>

        <button 
          className="btn btn-primary" 
          onClick={saveConfig}
          disabled={loading}
        >
          💾 Sauvegarder la configuration
        </button>
      </div>

      {/* Test de connexion */}
      <div className="test-section card">
        <h2>🔍 Test de connexion</h2>
        <p className="info-text">
          Testez la connexion à l'API Planif avec la configuration actuelle
        </p>

        <button 
          className="btn btn-secondary" 
          onClick={testConnection}
          disabled={loading}
        >
          {loading ? '⏳ Test en cours...' : '🧪 Tester la connexion'}
        </button>

        {testResult && (
          <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
            {testResult.success ? (
              <>
                <h3>✅ Connexion réussie!</h3>
                <p><strong>Token obtenu:</strong> {testResult.token_obtained ? 'Oui' : 'Non'}</p>
                <p><strong>Nombre d'agents:</strong> {testResult.agents_count || 0}</p>
                {testResult.sample_data && (
                  <div className="sample-data">
                    <p><strong>Exemple de données:</strong></p>
                    <pre>{JSON.stringify(testResult.sample_data, null, 2)}</pre>
                  </div>
                )}
              </>
            ) : (
              <>
                <h3>❌ Échec de la connexion</h3>
                <p>{testResult.message || testResult.error || 'Erreur inconnue'}</p>
                <div className="help-text">
                  <p><strong>Vérifiez:</strong></p>
                  <ul>
                    <li>Les URLs sont correctes selon le PDF</li>
                    <li>Les credentials sont valides</li>
                    <li>Le format d'authentification est correct</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Synchronisation */}
      <div className="sync-section card">
        <h2>🔄 Synchronisation des utilisateurs</h2>
        <p className="info-text">
          Importez tous les agents actifs depuis Planif vers la base de données
        </p>

        <button 
          className="btn btn-success" 
          onClick={syncUsers}
          disabled={loading || (testResult && !testResult.success)}
        >
          {loading ? '⏳ Synchronisation en cours...' : '🚀 Lancer la synchronisation'}
        </button>

        {syncResult && (
          <div className={`sync-result ${syncResult.success ? 'success' : 'error'}`}>
            {syncResult.success ? (
              <>
                <h3>✅ Synchronisation réussie!</h3>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-label">Total agents</span>
                    <span className="stat-value">{syncResult.stats?.total_agents || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Nouveaux utilisateurs</span>
                    <span className="stat-value success">{syncResult.stats?.imported || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Utilisateurs mis à jour</span>
                    <span className="stat-value info">{syncResult.stats?.updated || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Erreurs</span>
                    <span className="stat-value error">{syncResult.stats?.errors || 0}</span>
                  </div>
                </div>
                {syncResult.errors && syncResult.errors.length > 0 && (
                  <div className="errors-list">
                    <p><strong>Erreurs rencontrées:</strong></p>
                    <ul>
                      {syncResult.errors.slice(0, 10).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {syncResult.errors.length > 10 && (
                        <li>... et {syncResult.errors.length - 10} autres erreurs</li>
                      )}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <>
                <h3>❌ Échec de la synchronisation</h3>
                <p>{syncResult.message || syncResult.error || 'Erreur inconnue'}</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Messages d'erreur */}
      {error && (
        <div className="error-message">
          <strong>❌ Erreur:</strong> {error}
        </div>
      )}
    </div>
  );
};

export default PlanifSync;
